import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { VpsPageHeader, fmt, labelFollowRunner } from './vpsShared';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'all', label: 'All' },
  { id: 'custom', label: 'Custom' }
];

/** Forward on-demand sample window. */
const SAMPLE_MS = 3 * 60 * 1000;

function pct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function signed(n) {
  const v = Number(n) || 0;
  if (v > 0) return `+${v.toLocaleString()}`;
  return v.toLocaleString();
}

function rate(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(1);
}

/** Follows/hr → Follows/min */
function followsPerMin(avgFollowsPerHour) {
  if (avgFollowsPerHour == null || Number.isNaN(Number(avgFollowsPerHour))) return '—';
  return (Number(avgFollowsPerHour) / 60).toFixed(1);
}

function formatSampleRemaining(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const WS_STORAGE_KEY = 'wingr.vps.workspaceId';
const AGENT_STORAGE_KEY = 'wingr.vps.selectedAgentId';

function rememberAgent(id) {
  try {
    if (id) localStorage.setItem(AGENT_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

function readStoredWorkspace() {
  try {
    return localStorage.getItem(WS_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function workspaceHint(workspaceId, scrapeTag) {
  const ws = String(workspaceId || 'legacy');
  const db = ws === 'legacy' ? 'legacy' : 'this workspace';
  return scrapeTag ? `${db} · ${scrapeTag}` : db;
}

export default function VpsPerformance() {
  const { user } = useAuth();
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [filterAgentId, setFilterAgentId] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState(readStoredWorkspace);
  // Default is "active accounts only" (excludes captcha/suspended/auth_expired/
  // etc.) — matches what's actually being worked right now. Checking this
  // includes every attached account regardless of status.
  const [allAccounts, setAllAccounts] = useState(false);
  // Accounts that have sent COLD_WINDOW follows since their last follow-back.
  // The streak is period-independent, so it costs extra ledger reads and is
  // only requested while the filter is on.
  const [coldOnly, setColdOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('agents'); // agents | profiles | accounts
  const [samples, setSamples] = useState({});
  const sampleTimersRef = useRef({});
  const sampleAbortRef = useRef({});
  const samplesRef = useRef(samples);
  samplesRef.current = samples;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        period,
        from: period === 'custom' ? customFrom || undefined : undefined,
        to: period === 'custom' ? customTo || undefined : undefined,
        includeAccounts: tab === 'accounts' || tab === 'profiles' ? 1 : 0,
        allAccounts: allAccounts ? 1 : 0,
        includeCold: tab === 'accounts' ? 1 : 0,
        coldOnly: tab === 'accounts' && coldOnly ? 1 : 0
      };
      if (filterAgentId) params.agentId = filterAgentId;
      if (workspaceId) params.workspaceId = workspaceId;
      const res = await api.get('/admin/va/ai-agents/vps-performance', { params });
      if (res.data?.success) {
        setData((prev) => {
          // Keep prior per-account rows while on Per VPS so switching tabs
          // after a light fetch does not wipe a previously loaded table.
          if (
            tab === 'agents'
            && prev?.accounts?.length
            && !(res.data.accounts || []).length
          ) {
            return {
              ...res.data,
              accounts: prev.accounts,
              userProfiles: prev.userProfiles || res.data.userProfiles
            };
          }
          return res.data;
        });
      } else toast.error(res.data?.error || 'Failed to load performance');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load performance');
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo, filterAgentId, workspaceId, tab, allAccounts, coldOnly]);

  useEffect(() => {
    api.get('/scraper-v2/workspaces', { params: { counts: 0 } })
      .then((res) => {
        if (res.data?.success) setWorkspaces(res.data.workspaces || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(WS_STORAGE_KEY, workspaceId); } catch { /* ignore */ }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => {
    Object.values(sampleTimersRef.current).forEach((id) => clearInterval(id));
    sampleTimersRef.current = {};
    Object.keys(sampleAbortRef.current).forEach((k) => {
      sampleAbortRef.current[k] = true;
    });
  }, []);

  const cancelSample = useCallback((agentId) => {
    sampleAbortRef.current[agentId] = true;
    if (sampleTimersRef.current[agentId]) {
      clearInterval(sampleTimersRef.current[agentId]);
      delete sampleTimersRef.current[agentId];
    }
    setSamples((prev) => {
      const next = { ...prev };
      delete next[agentId];
      return next;
    });
  }, []);

  const startSample = useCallback(async (agentId) => {
    if (!agentId) return;
    const existing = samplesRef.current[agentId];
    if (existing?.status === 'running' || existing?.status === 'fetching') return;

    sampleAbortRef.current[agentId] = false;
    const from = new Date();
    const endsAt = from.getTime() + SAMPLE_MS;

    setSamples((prev) => ({
      ...prev,
      [agentId]: {
        status: 'running',
        from: from.toISOString(),
        endsAt,
        remainingMs: SAMPLE_MS
      }
    }));

    if (sampleTimersRef.current[agentId]) {
      clearInterval(sampleTimersRef.current[agentId]);
    }
    sampleTimersRef.current[agentId] = setInterval(() => {
      const left = endsAt - Date.now();
      if (left <= 0) {
        clearInterval(sampleTimersRef.current[agentId]);
        delete sampleTimersRef.current[agentId];
        setSamples((prev) => {
          const cur = prev[agentId];
          if (!cur || cur.status !== 'running') return prev;
          return {
            ...prev,
            [agentId]: { ...cur, remainingMs: 0, status: 'fetching' }
          };
        });
        return;
      }
      setSamples((prev) => {
        const cur = prev[agentId];
        if (!cur || cur.status !== 'running') return prev;
        return { ...prev, [agentId]: { ...cur, remainingMs: left } };
      });
    }, 250);

    await new Promise((resolve) => {
      const tick = () => {
        if (sampleAbortRef.current[agentId]) {
          resolve();
          return;
        }
        if (Date.now() >= endsAt) {
          resolve();
          return;
        }
        setTimeout(tick, 250);
      };
      tick();
    });

    if (sampleTimersRef.current[agentId]) {
      clearInterval(sampleTimersRef.current[agentId]);
      delete sampleTimersRef.current[agentId];
    }
    if (sampleAbortRef.current[agentId]) return;

    const to = new Date();
    setSamples((prev) => ({
      ...prev,
      [agentId]: {
        ...(prev[agentId] || {}),
        status: 'fetching',
        remainingMs: 0,
        from: from.toISOString()
      }
    }));

    try {
      const res = await api.get('/admin/va/ai-agents/vps-follow-sample', {
        params: {
          agentId,
          from: from.toISOString(),
          to: to.toISOString(),
          allAccounts: allAccounts ? 1 : 0,
          ...(workspaceId ? { workspaceId } : {})
        }
      });
      if (sampleAbortRef.current[agentId]) return;
      if (!res.data?.success) throw new Error(res.data?.error || 'Sample failed');
      setSamples((prev) => ({
        ...prev,
        [agentId]: {
          status: 'done',
          from: from.toISOString(),
          follows: res.data.follows,
          perMin: res.data.perMin,
          minutes: res.data.minutes
        }
      }));
    } catch (err) {
      if (sampleAbortRef.current[agentId]) return;
      const msg = err.response?.data?.error || err.message || 'Sample failed';
      setSamples((prev) => ({
        ...prev,
        [agentId]: { status: 'error', error: msg }
      }));
      toast.error(msg);
    }
  }, [allAccounts, workspaceId]);

  const overall = data?.overall || {};
  const agentRows = data?.agents || [];
  const accountRows = useMemo(() => {
    const rows = data?.accounts || [];
    if (!filterAgentId) return rows;
    return rows.filter((r) => String(r.agentId) === String(filterAgentId));
  }, [data?.accounts, filterAgentId]);
  const profileRows = useMemo(() => {
    const rows = data?.userProfiles || [];
    if (!filterAgentId) return rows;
    return rows
      .map((row) => {
        const slot = (row.perVps || []).find((p) => String(p.agentId) === String(filterAgentId));
        if (!slot) return null;
        return {
          ...row,
          accountCount: slot.accounts,
          follows: slot.follows,
          followBacks: slot.followBacks,
          followBackPct: slot.followBackPct
        };
      })
      .filter(Boolean);
  }, [data?.userProfiles, filterAgentId]);

  if (user?.role !== 'admin') {
    return <div className="text-sm text-gray-500">Admin access required.</div>;
  }

  return (
    <div className="space-y-6">
      <VpsPageHeader
        title="VPS Performance"
        subtitle="Ledger-based follows, follow-backs, DMs, and following/followers gains — not linked to monitoring."
        actions={(
          <button
            type="button"
            onClick={load}
            className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
          >
            <ArrowPathIcon className="w-4 h-4" /> Refresh
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <label className="text-sm inline-flex items-center gap-2">
          <span className="text-gray-500">Period</span>
          <select
            className="form-select text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </label>
        {period === 'custom' && (
          <>
            <input
              type="date"
              className="form-input text-sm"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <input
              type="date"
              className="form-input text-sm"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </>
        )}
        <label className="text-sm inline-flex items-center gap-2">
          <span className="text-gray-500">Database</span>
          <select
            className="form-select text-sm min-w-[14rem]"
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
          >
            <option value="">Agent&apos;s Database</option>
            {(workspaces.length
              ? workspaces
              : [{ workspaceId: 'legacy', name: 'Current data (legacy)' }]
            ).map((w) => (
              <option key={w.workspaceId} value={w.workspaceId}>
                {w.name}
                {w.followBacks != null ? ` — ${Number(w.followBacks).toLocaleString()} follow-backs` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm inline-flex items-center gap-2">
          <span className="text-gray-500">VPS / agent</span>
          <select
            className="form-select text-sm min-w-[12rem]"
            value={filterAgentId}
            onChange={(e) => {
              setFilterAgentId(e.target.value);
              if (e.target.value) rememberAgent(e.target.value);
            }}
          >
            <option value="">All agents</option>
            {(data?.agents || []).map((a) => (
              <option key={a.agentId} value={a.agentId}>{a.name}</option>
            ))}
          </select>
        </label>
        <label
          className="text-sm inline-flex items-center gap-2 cursor-pointer"
          title="When off, captcha/suspended/auth_expired/limited/soft-banned accounts are excluded from every stat and count."
        >
          <input
            type="checkbox"
            className="form-checkbox"
            checked={allAccounts}
            onChange={(e) => setAllAccounts(e.target.checked)}
          />
          <span className="text-gray-500">All accounts</span>
        </label>
        {tab === 'accounts' && (
          <label
            className="text-sm inline-flex items-center gap-2 cursor-pointer"
            title="Only accounts that have sent 100 follows since their last follow-back (or have never had one)."
          >
            <input
              type="checkbox"
              className="form-checkbox"
              checked={coldOnly}
              onChange={(e) => setColdOnly(e.target.checked)}
            />
            <span className="text-gray-500">No follow-backs in last 100</span>
          </label>
        )}
        {data?.period && (
          <span className="text-xs text-gray-500 ml-auto">
            {data.period.label}
            {data.period.rateHours != null
              ? ` · ${data.period.rateHours}h active`
              : data.period.hours != null
                ? ` · ${data.period.hours}h window`
                : ''}
            {data.period.activeHours != null
              && data.period.hours != null
              && data.period.id !== 'all'
              && Math.abs(Number(data.period.hours) - Number(data.period.activeHours)) > 0.05
              ? ` (${data.period.hours}h calendar)`
              : ''}
            {' · '}source: ledger · {workspaceId
              ? (workspaces.find((w) => w.workspaceId === workspaceId)?.name || workspaceId)
              : "agent's Database"}
          </span>
        )}
      </div>

      {loading && !data ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
            {[
              { label: 'Follows sent', value: fmt(overall.follows) },
              { label: 'Follow-backs', value: fmt(overall.followBacks) },
              { label: 'FB rate', value: pct(overall.followBackPct) },
              { label: 'DMs sent', value: fmt(overall.dmsSent) },
              { label: 'Following gain', value: signed(overall.followingGain) },
              { label: 'Followers gain', value: signed(overall.followersGain) },
              { label: 'Follows / min', value: followsPerMin(overall.avgFollowsPerHour) },
              { label: 'Followers / hr', value: rate(overall.avgFollowersPerHour) }
            ].map((c) => (
              <div
                key={c.label}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3"
              >
                <div className="text-[11px] uppercase tracking-wide text-gray-500">{c.label}</div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">
                  {c.value}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTab('agents')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                tab === 'agents'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              Per VPS
            </button>
            <button
              type="button"
              onClick={() => setTab('profiles')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                tab === 'profiles'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              Per user-profile
            </button>
            <button
              type="button"
              onClick={() => setTab('accounts')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                tab === 'accounts'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              Per account
            </button>
          </div>

          {tab === 'agents' ? (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Agent</th>
                    <th className="px-3 py-2 text-right font-medium">Follows</th>
                    <th className="px-3 py-2 text-right font-medium">FB</th>
                    <th className="px-3 py-2 text-right font-medium">DMs</th>
                    <th className="px-3 py-2 text-right font-medium">Following Δ</th>
                    <th className="px-3 py-2 text-right font-medium">Followers Δ</th>
                    <th className="px-3 py-2 text-right font-medium">Follows/min</th>
                    <th className="px-3 py-2 text-right font-medium">Followers/hr</th>
                    <th className="px-3 py-2 text-right font-medium">Follows/hr</th>
                    <th className="px-3 py-2 text-right font-medium">3‑min sample</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {agentRows.map((row) => {
                    const sample = samples[row.agentId];
                    return (
                    <tr key={row.agentId}>
                      <td className="px-3 py-2.5 font-medium">
                        <button
                          type="button"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline text-left"
                          onClick={() => {
                            setFilterAgentId(row.agentId);
                            rememberAgent(row.agentId);
                            setTab('accounts');
                          }}
                        >
                          {row.name}
                        </button>
                        <div className="text-[11px] text-gray-400 font-normal">
                          {row.online ? 'online' : 'offline'} · {fmt(row.accountCount)}
                          {allAccounts ? ' accts' : ' active accts'}
                          {' · '}{workspaceHint(row.workspaceId, row.scrapeTag)}
                          {row.gainsSource === 'ledger_proxy' ? ' · gains≈ledger' : ''}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.follows)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {fmt(row.followBacks)}
                        <span className="text-gray-400 text-xs ml-1">{pct(row.followBackPct)}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.dmsSent)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{signed(row.followingGain)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{signed(row.followersGain)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{followsPerMin(row.avgFollowsPerHour)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{rate(row.avgFollowersPerHour)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{rate(row.avgFollowsPerHour)}</td>
                      <td className="px-3 py-2.5 text-right">
                        {sample?.status === 'running' && (
                          <div className="inline-flex flex-col items-end gap-1">
                            <span className="tabular-nums text-amber-600 dark:text-amber-400 font-medium">
                              {formatSampleRemaining(sample.remainingMs)}
                            </span>
                            <button
                              type="button"
                              onClick={() => cancelSample(row.agentId)}
                              className="text-[11px] text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        {sample?.status === 'fetching' && (
                          <span className="text-xs text-gray-500">Counting…</span>
                        )}
                        {sample?.status === 'done' && (
                          <div className="inline-flex flex-col items-end gap-0.5">
                            <span className="tabular-nums font-medium text-gray-900 dark:text-white">
                              {fmt(sample.follows)}
                              <span className="text-gray-500 font-normal text-xs ml-1">
                                ({rate(sample.perMin)}/min)
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => startSample(row.agentId)}
                              className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              Re-sample
                            </button>
                          </div>
                        )}
                        {sample?.status === 'error' && (
                          <div className="inline-flex flex-col items-end gap-0.5">
                            <span className="text-[11px] text-red-500" title={sample.error}>Failed</span>
                            <button
                              type="button"
                              onClick={() => startSample(row.agentId)}
                              className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              Retry
                            </button>
                          </div>
                        )}
                        {!sample && (
                          <button
                            type="button"
                            onClick={() => startSample(row.agentId)}
                            className="px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                          >
                            Sample 3 min
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {!agentRows.length && (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-gray-500">No agents in this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : tab === 'profiles' ? (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">User profile</th>
                    <th className="px-3 py-2 text-left font-medium">Runner</th>
                    <th className="px-3 py-2 text-left font-medium">Device</th>
                    <th className="px-3 py-2 text-left font-medium">Hand</th>
                    <th className="px-3 py-2 text-right font-medium">Accounts</th>
                    <th className="px-3 py-2 text-right font-medium">Follows</th>
                    <th className="px-3 py-2 text-right font-medium">FB</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {profileRows.map((row) => (
                    <tr key={row.profileId || 'none'}>
                      <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">
                        {row.name}
                        {!filterAgentId && Array.isArray(row.perVps) && row.perVps.length > 1 && (
                          <div className="text-[11px] text-gray-400 font-normal mt-0.5">
                            {row.perVps.map((slot) => `${slot.agentName} ${fmt(slot.follows)}`).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">{row.followRunner ? labelFollowRunner(row.followRunner) : '—'}</td>
                      <td className="px-3 py-2.5 capitalize">{row.inputDevice || '—'}</td>
                      <td className="px-3 py-2.5 capitalize">{row.handedness || '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.accountCount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.follows)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {fmt(row.followBacks)}
                        <span className="text-gray-400 text-xs ml-1">{pct(row.followBackPct)}</span>
                      </td>
                    </tr>
                  ))}
                  {!profileRows.length && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                        No user-profile activity in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Account</th>
                    <th className="px-3 py-2 text-left font-medium">VPS</th>
                    <th className="px-3 py-2 text-right font-medium">Follows</th>
                    <th className="px-3 py-2 text-right font-medium">FB</th>
                    <th
                      className="px-3 py-2 text-right font-medium"
                      title="Follows sent since this account's last follow-back."
                    >
                      Cold streak
                    </th>
                    <th className="px-3 py-2 text-right font-medium">DMs</th>
                    <th className="px-3 py-2 text-right font-medium">Following Δ</th>
                    <th className="px-3 py-2 text-right font-medium">Followers Δ</th>
                    <th className="px-3 py-2 text-right font-medium">Follows/min</th>
                    <th className="px-3 py-2 text-right font-medium">Followers/hr</th>
                    <th className="px-3 py-2 text-right font-medium">Follows/hr</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {accountRows.map((row) => (
                    <tr key={`${row.agentId}:${row.accountId}`}>
                      <td className="px-3 py-2.5 font-mono">
                        @{row.username}
                        <div className="text-[11px] text-gray-400 font-sans">{row.status}</div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.agentName}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.follows)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {fmt(row.followBacks)}
                        <span className="text-gray-400 text-xs ml-1">{pct(row.followBackPct)}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {row.followsSinceLastFollowback == null ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <span className={row.cold ? 'text-red-500 font-medium' : ''}>
                            {row.followsSinceCapped
                              ? `${fmt(row.followsSinceLastFollowback)}+`
                              : fmt(row.followsSinceLastFollowback)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.dmsSent)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{signed(row.followingGain)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{signed(row.followersGain)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{followsPerMin(row.avgFollowsPerHour)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{rate(row.avgFollowersPerHour)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{rate(row.avgFollowsPerHour)}</td>
                    </tr>
                  ))}
                  {!accountRows.length && (
                    <tr>
                      <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                        {coldOnly
                          ? 'No accounts are cold — every account has had a follow-back within its last 100 follows.'
                          : 'No account activity in this period.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
